import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createElection, addCandidates } from '../services/api';
import './Pages.css';

const CreateElectionPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Election details
  const [electionData, setElectionData] = useState({
    title: '',
    description: '',
    electionType: 1, // Default to Single Choice
    maxCandidates: 5,
    maxSelections: 1,
    startTime: '',
    endTime: ''
  });
  
  // Candidates
  const [candidates, setCandidates] = useState([
    { name: '', description: '' },
    { name: '', description: '' }
  ]);
  
  // Created election ID (for step 2)
  const [electionId, setElectionId] = useState(null);

  const handleElectionChange = (e) => {
    const { name, value } = e.target;
    
    setElectionData({
      ...electionData,
      [name]: name === 'electionType' || name === 'maxCandidates' || name === 'maxSelections'
        ? parseInt(value, 10)
        : value
    });
    
    // Auto-update maxSelections for Single Choice
    if (name === 'electionType' && parseInt(value, 10) === 1) {
      setElectionData(prev => ({ ...prev, maxSelections: 1 }));
    }
  };

  const handleCandidateChange = (index, field, value) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index] = {
      ...updatedCandidates[index],
      [field]: value
    };
    setCandidates(updatedCandidates);
  };

  const addCandidateField = () => {
    setCandidates([...candidates, { name: '', description: '' }]);
  };

  const removeCandidateField = (index) => {
    if (candidates.length <= 2) return; // Keep at least 2 candidates
    
    const updatedCandidates = [...candidates];
    updatedCandidates.splice(index, 1);
    setCandidates(updatedCandidates);
  };

  const validateElectionData = () => {
    if (!electionData.title.trim()) {
      setError('Election title is required');
      return false;
    }
    
    if (electionData.maxCandidates < 2) {
      setError('Maximum candidates must be at least 2');
      return false;
    }
    
    if (electionData.maxSelections < 1) {
      setError('Maximum selections must be at least 1');
      return false;
    }
    
    if (electionData.maxSelections > electionData.maxCandidates) {
      setError('Maximum selections cannot exceed maximum candidates');
      return false;
    }
    
    // Validate dates if provided
    if (electionData.startTime && electionData.endTime) {
      const start = new Date(electionData.startTime);
      const end = new Date(electionData.endTime);
      
      if (end <= start) {
        setError('End time must be later than start time');
        return false;
      }
    }
    
    setError(null);
    return true;
  };

  const validateCandidates = () => {
    // Check for empty candidate names
    const emptyNames = candidates.some(c => !c.name.trim());
    if (emptyNames) {
      setError('All candidates must have a name');
      return false;
    }
    
    // Check for duplicate names
    const names = candidates.map(c => c.name.trim().toLowerCase());
    const uniqueNames = new Set(names);
    if (uniqueNames.size !== names.length) {
      setError('Candidate names must be unique');
      return false;
    }
    
    setError(null);
    return true;
  };

  const handleCreateElection = async (e) => {
    e.preventDefault();
    
    if (!validateElectionData()) return;
    
    setLoading(true);
    
    try {
      const response = await createElection(electionData);
      setElectionId(response.data.election.id);
      setSuccessMessage('Election created successfully! Now add candidates.');
      setStep(2);
      setError(null);
    } catch (err) {
      console.error('Error creating election:', err);
      setError(err.response?.data?.message || 'Failed to create election. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCandidates = async (e) => {
    e.preventDefault();
    
    if (!validateCandidates()) return;
    
    setLoading(true);
    
    try {
      // Filter out any empty candidates (shouldn't happen due to validation)
      const validCandidates = candidates.filter(c => c.name.trim());
      
      await addCandidates(electionId, validCandidates);
      setSuccessMessage('Candidates added successfully!');
      
      // Redirect to election details page
      setTimeout(() => {
        navigate(`/elections/${electionId}`);
      }, 1500);
    } catch (err) {
      console.error('Error adding candidates:', err);
      setError(err.response?.data?.message || 'Failed to add candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-election-page">
      <h1>Create New Election</h1>
      
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
      
      <div className="steps">
        <div className={`step ${step === 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-text">Election Details</div>
        </div>
        
        <div className="step-connector"></div>
        
        <div className={`step ${step === 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-text">Add Candidates</div>
        </div>
      </div>
      
      {step === 1 && (
        <form onSubmit={handleCreateElection} className="election-form card">
          <div className="form-group">
            <label htmlFor="title" className="form-label required">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-input"
              value={electionData.title}
              onChange={handleElectionChange}
              placeholder="e.g. Board Member Election"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea
              id="description"
              name="description"
              className="form-textarea"
              value={electionData.description}
              onChange={handleElectionChange}
              placeholder="Provide details about this election"
              rows="3"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="electionType" className="form-label required">Election Type</label>
              <select
                id="electionType"
                name="electionType"
                className="form-select"
                value={electionData.electionType}
                onChange={handleElectionChange}
                required
              >
                <option value={1}>Single Choice</option>
                <option value={2}>Multi Choice</option>
                <option value={3}>Ranked Choice</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="maxCandidates" className="form-label required">Max Candidates</label>
              <input
                type="number"
                id="maxCandidates"
                name="maxCandidates"
                className="form-input"
                value={electionData.maxCandidates}
                onChange={handleElectionChange}
                min="2"
                max="100"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="maxSelections" className="form-label required">Max Selections</label>
              <input
                type="number"
                id="maxSelections"
                name="maxSelections"
                className="form-input"
                value={electionData.maxSelections}
                onChange={handleElectionChange}
                min="1"
                max={electionData.maxCandidates}
                required
                disabled={electionData.electionType === 1} // Disabled for Single Choice
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startTime" className="form-label">Start Time</label>
              <input
                type="datetime-local"
                id="startTime"
                name="startTime"
                className="form-input"
                value={electionData.startTime}
                onChange={handleElectionChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="endTime" className="form-label">End Time</label>
              <input
                type="datetime-local"
                id="endTime"
                name="endTime"
                className="form-input"
                value={electionData.endTime}
                onChange={handleElectionChange}
              />
            </div>
          </div>
          
          <div className="election-help-text">
            <p>
              <strong>Election Types:</strong>
            </p>
            <ul>
              <li><strong>Single Choice:</strong> Voters select exactly one candidate.</li>
              <li><strong>Multi Choice:</strong> Voters can select up to the maximum number of selections.</li>
              <li><strong>Ranked Choice:</strong> Voters rank candidates in order of preference.</li>
            </ul>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Continue to Add Candidates'}
            </button>
          </div>
        </form>
      )}
      
      {step === 2 && (
        <form onSubmit={handleAddCandidates} className="candidates-form card">
          <h2>Add Candidates</h2>
          
          <p className="form-instruction">
            Add at least two candidates for the election. Each candidate must have a unique name.
          </p>
          
          {candidates.map((candidate, index) => (
            <div key={index} className="candidate-form-group">
              <div className="candidate-header">
                <div className="candidate-number">Candidate {index + 1}</div>
                {index >= 2 && (
                  <button 
                    type="button" 
                    className="btn-remove" 
                    onClick={() => removeCandidateField(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`candidate-name-${index}`} className="form-label required">Name</label>
                  <input
                    type="text"
                    id={`candidate-name-${index}`}
                    className="form-input"
                    value={candidate.name}
                    onChange={(e) => handleCandidateChange(index, 'name', e.target.value)}
                    placeholder="Candidate name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor={`candidate-desc-${index}`} className="form-label">Description</label>
                  <input
                    type="text"
                    id={`candidate-desc-${index}`}
                    className="form-input"
                    value={candidate.description}
                    onChange={(e) => handleCandidateChange(index, 'description', e.target.value)}
                    placeholder="Short description (optional)"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <div className="add-candidate-container">
            <button 
              type="button" 
              className="btn btn-outline btn-add"
              onClick={addCandidateField}
            >
              + Add Another Candidate
            </button>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-outline"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Back to Election Details
            </button>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Adding Candidates...' : 'Complete Election Setup'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateElectionPage;